<template>
  <div>
    <Navbar />

    <main>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Hero                                                          -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="hero-section">
        <div class="page-container">
          <div class="hero-grid">
            <div class="hero-content">
              <div class="hero-badge">
                <font-awesome-icon icon="star" class="me-1" />
                All-in-One Business Management Platform
              </div>
              <h1 class="hero-title">
                Run Every Part of Your Business with <span class="accent">Mapiibs</span>.
              </h1>
              <p class="hero-sub">
                One platform. 17 integrated modules. From inventory and sales to accounting,
                payroll, HR, procurement, loans, invoicing and beyond — everything your
                business needs to operate, scale, and succeed.
              </p>
              <div class="hero-actions">
                <router-link to="/auth/register" class="btn-primary-lg">
                  Get Started Free <font-awesome-icon icon="arrow-right" class="ms-2" />
                </router-link>
                <a href="#demo" class="btn-ghost-lg">Book a Demo</a>
              </div>
            </div>
            <div class="hero-visual">
              <img src="static/img/cat-v2-8.svg" alt="Mapiibs Dashboard" />
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Stats bar                                                     -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="stats-bar">
        <div class="page-container">
          <div class="stats-grid">
            <div class="stat-item">
              <span class="stat-number">17+</span>
              <span class="stat-label">Integrated Modules</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-number">Multi-Currency</span>
              <span class="stat-label">Global-Ready Operations</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-number">Multi-Location</span>
              <span class="stat-label">Warehouse & Branch Support</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-number">100% Cloud</span>
              <span class="stat-label">Access Anywhere, Anytime</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- About / Intro                                                 -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad">
        <div class="page-container">
          <div class="split-grid">
            <div class="split-content">
              <p class="section-eyebrow">Why Mapiibs</p>
              <h2 class="section-title">Built for the Way Real Businesses Work</h2>
              <p class="section-body" v-html="setup.short_description || 'Mapiibs is a modular ERP designed to unify every department under one roof — eliminating data silos, manual errors, and costly disconnected tools.'"></p>
              <ul class="check-list">
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Unified accounting, payroll &amp; HR</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Real-time inventory &amp; stock control</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Sales, purchases, invoicing &amp; billing</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Asset management &amp; depreciation</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Shift &amp; rota scheduling</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Debtors, creditors &amp; loan management</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Built-in team chat &amp; file sharing</li>
              </ul>
              <router-link to="/auth/register" class="btn-primary-md">Start for Free</router-link>
            </div>
            <div class="split-visual">
              <img src="/static/img/cat-v2-7.svg" alt="Mapiibs features" />
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- All Modules grid                                              -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad section-gray" id="modules">
        <div class="page-container">
          <div class="section-header">
            <p class="section-eyebrow">Complete Business Suite</p>
            <h2 class="section-title">Everything Your Business Needs, In One Platform</h2>
            <p class="section-body">From day-to-day operations to strategic financial reporting, every module is designed to work seamlessly with the others.</p>
          </div>
          <div class="modules-grid">
            <div v-for="mod in modules" :key="mod.title" class="module-card">
              <div class="module-icon-wrap" :style="{ background: mod.bg }">
                <font-awesome-icon :icon="mod.icon" class="module-icon" :style="{ color: mod.color }" />
              </div>
              <div class="module-body">
                <h5 class="module-title">{{ mod.title }}</h5>
                <p class="module-desc">{{ mod.desc }}</p>
                <ul class="module-features">
                  <li v-for="f in mod.features" :key="f">
                    <font-awesome-icon icon="check" class="mf-check" /> {{ f }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Spotlight 1 — Finance & Accounting                           -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad">
        <div class="page-container">
          <div class="split-grid">
            <div class="split-content">
              <p class="section-eyebrow">Finance &amp; Accounting</p>
              <h2 class="section-title">Complete Financial Control, From Journals to Payables</h2>
              <p class="section-body">
                Mapiibs gives you a fully integrated financial back-office. Double-entry accounting,
                automated journal entries, invoice management, billing cycles, and full visibility
                over your debtors and creditors — all in one place.
              </p>
              <div class="spotlight-cards">
                <div v-for="item in financeSpotlight" :key="item.title" class="spotlight-card">
                  <div class="spotlight-card-icon" :style="{ background: item.bg, color: item.color }">
                    <font-awesome-icon :icon="item.icon" />
                  </div>
                  <div>
                    <div class="spotlight-card-title">{{ item.title }}</div>
                    <div class="spotlight-card-desc">{{ item.desc }}</div>
                  </div>
                </div>
              </div>
              <router-link to="/auth/register" class="btn-primary-md">Explore Finance Tools</router-link>
            </div>
            <div class="split-visual">
              <div class="spotlight-visual-card">
                <div class="svc-header">
                  <font-awesome-icon icon="calculator" class="svc-header-icon" />
                  <span>Financial Overview</span>
                </div>
                <div class="svc-metric-row">
                  <div class="svc-metric">
                    <span class="svc-metric-label">Total Revenue</span>
                    <span class="svc-metric-value accent">£84,230.00</span>
                  </div>
                  <div class="svc-metric">
                    <span class="svc-metric-label">Outstanding Invoices</span>
                    <span class="svc-metric-value" style="color:#d97706;">£12,400.00</span>
                  </div>
                </div>
                <div class="svc-metric-row">
                  <div class="svc-metric">
                    <span class="svc-metric-label">Creditors Balance</span>
                    <span class="svc-metric-value" style="color:#dc2626;">£5,800.00</span>
                  </div>
                  <div class="svc-metric">
                    <span class="svc-metric-label">Net Profit</span>
                    <span class="svc-metric-value" style="color:#16a34a;">£21,050.00</span>
                  </div>
                </div>
                <div class="svc-progress-row">
                  <span class="svc-progress-label">Budget utilisation</span>
                  <div class="svc-progress-bar"><div class="svc-progress-fill" style="width:68%;"></div></div>
                  <span class="svc-progress-pct">68%</span>
                </div>
                <div class="svc-tag-row">
                  <span class="svc-tag svc-tag--green">Accounts Payable</span>
                  <span class="svc-tag svc-tag--blue">Journals</span>
                  <span class="svc-tag svc-tag--purple">Trial Balance</span>
                  <span class="svc-tag svc-tag--orange">Invoices</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Spotlight 2 — HR & Payroll                                   -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad section-gray">
        <div class="page-container">
          <div class="split-grid split-grid--reverse">
            <div class="split-content">
              <p class="section-eyebrow">HR &amp; Payroll</p>
              <h2 class="section-title">Manage Your Workforce Without the Headaches</h2>
              <p class="section-body">
                From hiring to payslip — Mapiibs handles it all. Manage employee records,
                departments, leave and attendance. Run automated payroll with custom earnings and
                deductions. Handle employee loan requests and repayments, all tied directly into
                payroll so nothing slips through the cracks.
              </p>
              <div class="spotlight-cards">
                <div v-for="item in hrSpotlight" :key="item.title" class="spotlight-card">
                  <div class="spotlight-card-icon" :style="{ background: item.bg, color: item.color }">
                    <font-awesome-icon :icon="item.icon" />
                  </div>
                  <div>
                    <div class="spotlight-card-title">{{ item.title }}</div>
                    <div class="spotlight-card-desc">{{ item.desc }}</div>
                  </div>
                </div>
              </div>
              <router-link to="/auth/register" class="btn-primary-md">Explore HR &amp; Payroll</router-link>
            </div>
            <div class="split-visual">
              <div class="spotlight-visual-card">
                <div class="svc-header">
                  <font-awesome-icon icon="users" class="svc-header-icon" style="color:#dc2626;" />
                  <span>Payroll Summary</span>
                </div>
                <div class="svc-payroll-list">
                  <div v-for="emp in payrollPreview" :key="emp.name" class="svc-payroll-row">
                    <div class="svc-emp-avatar">{{ emp.initials }}</div>
                    <div class="svc-emp-info">
                      <span class="svc-emp-name">{{ emp.name }}</span>
                      <span class="svc-emp-role">{{ emp.role }}</span>
                    </div>
                    <span class="svc-emp-pay">{{ emp.pay }}</span>
                  </div>
                </div>
                <div class="svc-hr-tags">
                  <span class="svc-tag svc-tag--red">HR Management</span>
                  <span class="svc-tag svc-tag--orange">Payroll</span>
                  <span class="svc-tag svc-tag--green">Loans</span>
                  <span class="svc-tag svc-tag--blue">Leave Tracking</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Spotlight 3 — Operations                                      -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad">
        <div class="page-container">
          <div class="split-grid">
            <div class="split-content">
              <p class="section-eyebrow">Operations</p>
              <h2 class="section-title">Inventory, Sales &amp; Purchases — All Connected</h2>
              <p class="section-body">
                Real-time stock visibility across every warehouse and location. Raise purchase orders,
                receive goods, process sales with POS or order management, and reverse transactions
                when needed. Barcodes, expiry tracking, batch management — all built in.
              </p>
              <div class="spotlight-cards">
                <div v-for="item in opsSpotlight" :key="item.title" class="spotlight-card">
                  <div class="spotlight-card-icon" :style="{ background: item.bg, color: item.color }">
                    <font-awesome-icon :icon="item.icon" />
                  </div>
                  <div>
                    <div class="spotlight-card-title">{{ item.title }}</div>
                    <div class="spotlight-card-desc">{{ item.desc }}</div>
                  </div>
                </div>
              </div>
              <router-link to="/auth/register" class="btn-primary-md">Explore Operations</router-link>
            </div>
            <div class="split-visual">
              <div class="spotlight-visual-card">
                <div class="svc-header">
                  <font-awesome-icon icon="boxes" class="svc-header-icon" style="color:#004AAD;" />
                  <span>Inventory at a Glance</span>
                </div>
                <div class="svc-inv-list">
                  <div v-for="item in inventoryPreview" :key="item.name" class="svc-inv-row">
                    <div class="svc-inv-name">{{ item.name }}</div>
                    <div class="svc-inv-bar-wrap">
                      <div class="svc-inv-bar">
                        <div class="svc-inv-fill" :style="{ width: item.pct + '%', background: item.color }"></div>
                      </div>
                    </div>
                    <span class="svc-inv-qty" :style="{ color: item.color }">{{ item.qty }}</span>
                  </div>
                </div>
                <div class="svc-hr-tags">
                  <span class="svc-tag svc-tag--blue">Inventory</span>
                  <span class="svc-tag svc-tag--green">Sales</span>
                  <span class="svc-tag svc-tag--orange">Purchases</span>
                  <span class="svc-tag svc-tag--purple">Barcode</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Spotlight 4 — Procurement                                    -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad section-gray">
        <div class="page-container">
          <div class="split-grid split-grid--reverse">
            <div class="split-content">
              <p class="section-eyebrow">Procurement</p>
              <h2 class="section-title">From Requisition to Delivery — Fully Controlled</h2>
              <p class="section-body">
                Mapiibs Procurement gives you end-to-end control over your purchasing cycle.
                Build your vendor directory, raise and approve purchase requisitions, run
                competitive sourcing events, convert approved orders to GRNs, and track
                every penny of procurement spend in one place.
              </p>
              <div class="spotlight-cards">
                <div v-for="item in procurementSpotlight" :key="item.title" class="spotlight-card">
                  <div class="spotlight-card-icon" :style="{ background: item.bg, color: item.color }">
                    <font-awesome-icon :icon="item.icon" />
                  </div>
                  <div>
                    <div class="spotlight-card-title">{{ item.title }}</div>
                    <div class="spotlight-card-desc">{{ item.desc }}</div>
                  </div>
                </div>
              </div>
              <router-link to="/auth/register" class="btn-primary-md">Explore Procurement</router-link>
            </div>
            <div class="split-visual">
              <div class="spotlight-visual-card">
                <div class="svc-header">
                  <font-awesome-icon icon="clipboard-list" class="svc-header-icon" style="color:#0891b2;" />
                  <span>Procurement Pipeline</span>
                </div>
                <div class="svc-po-list">
                  <div v-for="po in procurementPreview" :key="po.ref" class="svc-po-row">
                    <div class="svc-po-ref">{{ po.ref }}</div>
                    <div class="svc-emp-info">
                      <span class="svc-emp-name">{{ po.item }}</span>
                      <span class="svc-emp-role">{{ po.vendor }}</span>
                    </div>
                    <span class="svc-tag" :class="po.cls">{{ po.status }}</span>
                  </div>
                </div>
                <div class="svc-hr-tags">
                  <span class="svc-tag svc-tag--blue">Vendors</span>
                  <span class="svc-tag svc-tag--orange">Requisitions</span>
                  <span class="svc-tag svc-tag--green">Purchase Orders</span>
                  <span class="svc-tag svc-tag--purple">Sourcing Events</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Spotlight 5 — Asset Management                               -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad">
        <div class="page-container">
          <div class="split-grid split-grid--reverse">
            <div class="split-content">
              <p class="section-eyebrow">Asset Management</p>
              <h2 class="section-title">Track, Manage &amp; Optimise Every Business Asset</h2>
              <p class="section-body">
                From purchase to disposal, Mapiibs gives you complete control over your company assets.
                Register and categorise assets, run automated depreciation calculations, track allocation
                across teams and locations, and stay on top of maintenance schedules — all in one place.
              </p>
              <div class="spotlight-cards">
                <div v-for="item in assetSpotlight" :key="item.title" class="spotlight-card">
                  <div class="spotlight-card-icon" :style="{ background: item.bg, color: item.color }">
                    <font-awesome-icon :icon="item.icon" />
                  </div>
                  <div>
                    <div class="spotlight-card-title">{{ item.title }}</div>
                    <div class="spotlight-card-desc">{{ item.desc }}</div>
                  </div>
                </div>
              </div>
              <router-link to="/auth/register" class="btn-primary-md">Explore Asset Management</router-link>
            </div>
            <div class="split-visual">
              <div class="spotlight-visual-card">
                <div class="svc-header">
                  <font-awesome-icon icon="layer-group" class="svc-header-icon" style="color:#7c3aed;" />
                  <span>Asset Overview</span>
                </div>
                <div class="svc-metric-row">
                  <div class="svc-metric">
                    <span class="svc-metric-label">Total Assets</span>
                    <span class="svc-metric-value" style="color:#7c3aed;">248</span>
                  </div>
                  <div class="svc-metric">
                    <span class="svc-metric-label">Net Book Value</span>
                    <span class="svc-metric-value accent">£1.2M</span>
                  </div>
                </div>
                <div class="svc-inv-list">
                  <div v-for="a in assetPreview" :key="a.name" class="svc-inv-row">
                    <div class="svc-inv-name">{{ a.name }}</div>
                    <div class="svc-inv-bar-wrap">
                      <div class="svc-inv-bar">
                        <div class="svc-inv-fill" :style="{ width: a.pct + '%', background: a.color }"></div>
                      </div>
                    </div>
                    <span class="svc-inv-qty" :style="{ color: a.color }">{{ a.count }}</span>
                  </div>
                </div>
                <div class="svc-hr-tags">
                  <span class="svc-tag svc-tag--purple">Depreciation</span>
                  <span class="svc-tag svc-tag--blue">Allocation</span>
                  <span class="svc-tag svc-tag--orange">Maintenance</span>
                  <span class="svc-tag svc-tag--green">Disposal</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Spotlight 6 — Team Chat                                       -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad">
        <div class="page-container">
          <div class="split-grid">
            <div class="split-content">
              <p class="section-eyebrow">Team Communication</p>
              <h2 class="section-title">Keep Your Team Connected Without Leaving the Platform</h2>
              <p class="section-body">
                Stop switching between apps. Mapiibs has built-in team messaging — direct messages,
                group chats, and real-time file sharing all within your business workspace.
                Every conversation is tied to your team so context is never lost.
              </p>
              <div class="spotlight-cards">
                <div v-for="item in chatSpotlight" :key="item.title" class="spotlight-card">
                  <div class="spotlight-card-icon" :style="{ background: item.bg, color: item.color }">
                    <font-awesome-icon :icon="item.icon" />
                  </div>
                  <div>
                    <div class="spotlight-card-title">{{ item.title }}</div>
                    <div class="spotlight-card-desc">{{ item.desc }}</div>
                  </div>
                </div>
              </div>
              <router-link to="/auth/register" class="btn-primary-md">Explore Team Chat</router-link>
            </div>
            <div class="split-visual">
              <div class="spotlight-visual-card">
                <div class="svc-header">
                  <font-awesome-icon icon="comments" class="svc-header-icon" style="color:#0891b2;" />
                  <span>Team Conversations</span>
                </div>
                <div class="svc-chat-list">
                  <div v-for="msg in chatPreview" :key="msg.name" class="svc-chat-msg">
                    <div class="svc-emp-avatar" :style="{ background: msg.color }">{{ msg.initials }}</div>
                    <div class="svc-chat-bubble">
                      <div class="svc-emp-name">{{ msg.name }}</div>
                      <div class="svc-chat-text">{{ msg.text }}</div>
                    </div>
                    <span class="svc-chat-time">{{ msg.time }}</span>
                  </div>
                </div>
                <div class="svc-hr-tags">
                  <span class="svc-tag svc-tag--blue">Direct Messages</span>
                  <span class="svc-tag svc-tag--green">Groups</span>
                  <span class="svc-tag svc-tag--purple">File Sharing</span>
                  <span class="svc-tag svc-tag--orange">Real-time</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Spotlight 7 — Shift & Rota                                    -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad section-gray">
        <div class="page-container">
          <div class="split-grid split-grid--reverse">
            <div class="split-content">
              <p class="section-eyebrow">Shift &amp; Rota</p>
              <h2 class="section-title">Plan, Publish &amp; Manage Shifts Without the Chaos</h2>
              <p class="section-body">
                Build weekly rotas, assign shifts to employees, and track availability — all
                from inside Mapiibs. Employees get notified of their schedules instantly,
                and managers get a clear view of coverage across every department and location.
              </p>
              <div class="spotlight-cards">
                <div v-for="item in shiftSpotlight" :key="item.title" class="spotlight-card">
                  <div class="spotlight-card-icon" :style="{ background: item.bg, color: item.color }">
                    <font-awesome-icon :icon="item.icon" />
                  </div>
                  <div>
                    <div class="spotlight-card-title">{{ item.title }}</div>
                    <div class="spotlight-card-desc">{{ item.desc }}</div>
                  </div>
                </div>
              </div>
              <router-link to="/auth/register" class="btn-primary-md">Explore Shift &amp; Rota</router-link>
            </div>
            <div class="split-visual">
              <div class="spotlight-visual-card">
                <div class="svc-header">
                  <font-awesome-icon icon="calendar-week" class="svc-header-icon" style="color:#059669;" />
                  <span>Weekly Rota</span>
                </div>
                <div class="svc-rota-list">
                  <div v-for="shift in rotaPreview" :key="shift.name" class="svc-rota-row">
                    <div class="svc-emp-avatar" :style="{ background: shift.color }">{{ shift.initials }}</div>
                    <div class="svc-emp-info">
                      <span class="svc-emp-name">{{ shift.name }}</span>
                      <span class="svc-emp-role">{{ shift.shift }}</span>
                    </div>
                    <span class="svc-rota-badge" :class="shift.cls">{{ shift.day }}</span>
                  </div>
                </div>
                <div class="svc-hr-tags">
                  <span class="svc-tag svc-tag--green">Shifts</span>
                  <span class="svc-tag svc-tag--blue">Rota</span>
                  <span class="svc-tag svc-tag--orange">Availability</span>
                  <span class="svc-tag svc-tag--purple">Notifications</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Spotlight 8 — Visitor Management                              -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad">
        <div class="page-container">
          <div class="split-grid">
            <div class="split-content">
              <p class="section-eyebrow">Visitor Management</p>
              <h2 class="section-title">Know Exactly Who's In Your Building, Every Moment</h2>
              <p class="section-body">
                Replace the paper sign-in book with a smart, digital visitor flow. Employees pre-register
                guests, visitors check in via QR code, and your reception team gets a live dashboard of
                everyone on-site. Every visit is logged, timestamped, and auditable.
              </p>
              <div class="spotlight-cards">
                <div v-for="item in visitorSpotlight" :key="item.title" class="spotlight-card">
                  <div class="spotlight-card-icon" :style="{ background: item.bg, color: item.color }">
                    <font-awesome-icon :icon="item.icon" />
                  </div>
                  <div>
                    <div class="spotlight-card-title">{{ item.title }}</div>
                    <div class="spotlight-card-desc">{{ item.desc }}</div>
                  </div>
                </div>
              </div>
              <router-link to="/auth/register" class="btn-primary-md">Explore Visitor Management</router-link>
            </div>
            <div class="split-visual">
              <div class="spotlight-visual-card">
                <div class="svc-header">
                  <font-awesome-icon icon="id-badge" class="svc-header-icon" style="color:#0f766e;" />
                  <span>Reception Dashboard</span>
                </div>
                <div class="svc-metric-row">
                  <div class="svc-metric">
                    <span class="svc-metric-label">Today's Visitors</span>
                    <span class="svc-metric-value" style="color:#0f766e;">12</span>
                  </div>
                  <div class="svc-metric">
                    <span class="svc-metric-label">On-site Now</span>
                    <span class="svc-metric-value accent">4</span>
                  </div>
                </div>
                <div class="svc-inv-list">
                  <div v-for="v in visitorPreview" :key="v.name" class="svc-visitor-row">
                    <div class="svc-emp-avatar" style="background:#0f766e;">{{ v.initials }}</div>
                    <div class="svc-visitor-info">
                      <div class="svc-emp-name">{{ v.name }}</div>
                      <div class="svc-visitor-host">Host: {{ v.host }} · {{ v.time }}</div>
                    </div>
                    <span class="svc-tag" :class="v.cls">{{ v.status }}</span>
                  </div>
                </div>
                <div class="svc-hr-tags">
                  <span class="svc-tag svc-tag--teal">Pre-Registration</span>
                  <span class="svc-tag svc-tag--blue">QR Check-In</span>
                  <span class="svc-tag svc-tag--orange">Badge</span>
                  <span class="svc-tag svc-tag--purple">Audit Log</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Value props                                                   -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad section-gray">
        <div class="page-container">
          <div class="split-grid">
            <div class="split-content">
              <p class="section-eyebrow">Why teams choose us</p>
              <h2 class="section-title">Built for Performance, Designed for Growth</h2>
              <p class="section-body">Mapiibs removes the friction between your business processes, giving your team one source of truth across every department.</p>
              <router-link to="/auth/register" class="btn-primary-md">Get Started</router-link>
            </div>
            <div class="value-props">
              <div v-for="prop in valueProps" :key="prop.title" class="value-prop">
                <div class="value-dot" :style="{ background: prop.color }">
                  <font-awesome-icon icon="check" />
                </div>
                <div>
                  <h6 class="value-title">{{ prop.title }}</h6>
                  <p class="value-desc">{{ prop.desc }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- CTA band                                                      -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad cta-band">
        <div class="page-container cta-band-inner">
          <div>
            <h2 class="cta-title">One Platform. Every Department. Zero Compromises.</h2>
            <p class="cta-sub">Stop juggling disconnected tools. Bring your entire business onto Mapiibs today.</p>
          </div>
          <router-link to="/auth/register" class="btn-primary-lg btn-white">Start for Free</router-link>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Pillars                                                       -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad">
        <div class="page-container">
          <div class="section-header">
            <h2 class="section-title">Enterprise-Grade Infrastructure, SME-Friendly Pricing</h2>
            <p class="section-body">We've built Mapiibs on the same principles that power enterprise software — without the complexity or cost.</p>
          </div>
          <div class="pillars-grid">
            <div v-for="pillar in pillars" :key="pillar.title" class="pillar-card">
              <img :src="pillar.img" :alt="pillar.title" class="pillar-img" />
              <h5 class="pillar-title">{{ pillar.title }}</h5>
              <p class="pillar-desc">{{ pillar.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Pricing — commented out; replaced with quote CTA             -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <!--
      <section class="section-pad section-gray" id="pricing">
        <div class="page-container">
          <div class="section-header">
            <h2 class="section-title">Simple, Transparent Pricing</h2>
            <p class="section-body">Choose a plan that fits your business. Scale up as you grow — no hidden fees, no surprises.</p>
          </div>

          <div v-if="plansLoading" class="plans-loading">
            <div class="spinner-border text-primary" style="width:2.5rem;height:2.5rem;" role="status"></div>
            <p class="plans-loading-text">Loading plans...</p>
          </div>

          <template v-else>
            <div class="billing-tabs">
              <button
                v-for="cycle in billingCycles"
                :key="cycle.key"
                class="billing-tab"
                :class="{ 'billing-tab--active': activeCycle === cycle.key }"
                @click="activeCycle = cycle.key"
              >
                {{ cycle.label }}
              </button>
            </div>

            <div v-if="!activePlans.length" class="plans-empty">
              No plans available for this billing cycle.
            </div>

            <div v-else class="plans-grid">
              <div
                v-for="plan in activePlans"
                :key="plan.id"
                class="plan-card"
                :class="{ 'plan-card--popular': plan.is_popular }"
              >
                <div v-if="plan.is_popular" class="popular-badge">Most Popular</div>
                <div class="plan-name">{{ plan.name }}</div>
                <div class="plan-price">
                  <span class="plan-currency">{{ currencySymbol }}</span>
                  <span class="plan-amount">{{ formatCurrency(plan.amount) }}</span>
                </div>
                <div class="plan-cycle">per {{ plan.type }}</div>
                <ul class="plan-features">
                  <li v-for="(desc, i) in planFeatures(plan)" :key="i">
                    <font-awesome-icon icon="check" class="plan-check" /> {{ desc }}
                  </li>
                </ul>
                <button class="plan-cta" @click="paymentModal(plan)">Get Started</button>
              </div>
            </div>
          </template>
        </div>
      </section>
      -->

      <!-- Quote CTA (replaces pricing section) -->
      <section class="section-pad section-gray" id="pricing">
        <div class="page-container">
          <div class="quote-cta-wrap">
            <div class="quote-cta-icon">
              <font-awesome-icon icon="tag" />
            </div>
            <h2 class="quote-cta-title">Get a Custom Quote for Your Business</h2>
            <p class="quote-cta-body">
              Every business is unique. Contact us so we can understand your operations and recommend the right module setup tailored to your specific needs — with pricing to match.
            </p>
            <router-link to="/pricing" class="btn-quote-cta">
              Request a Quote <font-awesome-icon icon="arrow-right" class="ms-2" />
            </router-link>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Demo / Enquiry Form                                          -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad" id="demo">
        <div class="page-container">
          <div class="demo-wrap">
            <div class="demo-copy">
              <p class="section-eyebrow">Book a demo</p>
              <h2 class="section-title">See Mapiibs in Action</h2>
              <p class="section-body">Fill in the form and our team will reach out to schedule your free, personalised demo session.</p>
              <ul class="demo-perks">
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Free 30-minute walkthrough</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Tailored to your business needs</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> No commitment required</li>
              </ul>
            </div>
            <div class="demo-form-card">

              <div v-if="enquirySent" class="enquiry-success">
                <div class="enquiry-success-icon">
                  <font-awesome-icon icon="check-circle" />
                </div>
                <h4 class="enquiry-success-title">Request Received!</h4>
                <p class="enquiry-success-sub">Thank you! Our team will reach out within 24 hours to schedule your demo.</p>
                <button class="btn-primary-md" @click="enquirySent = false">Send Another</button>
              </div>

              <form v-else @submit.prevent="sendEnquiry">
                <h4 class="form-card-title">Request a Free Demo</h4>
                <div class="form-row-2">
                  <div class="form-group-pub">
                    <label class="form-label-pub">Your Name</label>
                    <input type="text" v-model="enquiry_name" class="form-input-pub" placeholder="John Doe" required />
                  </div>
                  <div class="form-group-pub">
                    <label class="form-label-pub">Email Address</label>
                    <input type="email" v-model="enquiry_email" class="form-input-pub" placeholder="john@company.com" required />
                  </div>
                </div>
                <div class="form-group-pub">
                  <label class="form-label-pub">Phone Number</label>
                  <input type="tel" v-model="enquiry_tel" class="form-input-pub" placeholder="+1 555 000 0000" required />
                </div>
                <div class="form-group-pub">
                  <label class="form-label-pub">Message</label>
                  <textarea v-model="enquiry_message" class="form-input-pub" rows="4" placeholder="Tell us about your business..." required></textarea>
                </div>
                <button type="submit" class="btn-primary-lg w-full" :disabled="enquiryLoading">
                  <font-awesome-icon v-if="enquiryLoading" icon="spinner" spin class="me-1" />
                  {{ enquiryLoading ? 'Sending...' : 'Request a Free Demo' }}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

    </main>

    <Footer />

    <PricingModals
      :loginModalOpen="loginModalOpen"
      :paymentModalOpen="paymentModalOpen"
      :currentData="currentData"
      :login="login"
      :validationErrors="validationErrors"
      :loginLoading="loginLoading"
      :countryCode="countryCode"
      :currencySymbol="currencySymbol"
      :localPaymentChannels="localPaymentChannels"
      :internationalPaymentChannels="internationalPaymentChannels"
      :backendUrl="backend_url.hostName"
      @close-login="loginModalOpen = false"
      @close-payment="paymentModalOpen = false"
      @update:login="login = $event"
      @login="loginUser"
      @flutterwave="makeFlutterwavePayment($event.data, $event.channel)"
      @paystack="makePaystackPayment($event.data, $event.channel)"
      @stripe="payWithStripe($event.data, $event.channel)"
    />

  </div>
</template>

<script>
import Navbar        from '../components/NavbarHome';
import Footer        from '../components/footer';
import PricingModals from '@/components/PricingModals.vue';
import axios         from 'axios';
import { pricingMixin }  from '@/lib/PricingMixin';
import { reusableMixin } from '../lib/reusableMixin';
import { useToast }      from 'vue-toastification';

export default {
  name: 'HomePage',

  components: { Navbar, Footer, PricingModals },

  mixins: [pricingMixin, reusableMixin],

  data() {
    return {
      setup:          {},
      plansLoading:   true,
      enquiryLoading: false,
      enquirySent:    false,
      toast:          useToast(),

      enquiry_name:    '',
      enquiry_email:   '',
      enquiry_tel:     '',
      enquiry_message: '',

      // ── All Modules ─────────────────────────────────────────────────
      modules: [
        {
          icon: 'boxes', color: '#004AAD', bg: 'rgba(0,74,173,.1)',
          title: 'Inventory Management',
          desc: 'Real-time stock control across multiple warehouses and locations with barcodes, expiry tracking, and automated reorder alerts.',
          features: ['Multi-location stock control', 'Barcode, batch & expiry tracking', 'Adjustments, transfers & write-offs'],
        },
        {
          icon: 'shopping-cart', color: '#16a34a', bg: 'rgba(22,163,74,.1)',
          title: 'Sales Management',
          desc: 'Manage the full sales cycle from quotation to fulfilment with POS support, credit sales, and transaction reversals.',
          features: ['Sales orders & point-of-sale', 'Sales reversals & returns', 'Customer credit limits'],
        },
        {
          icon: 'truck', color: '#ea580c', bg: 'rgba(234,88,12,.1)',
          title: 'Purchase Management',
          desc: 'Streamline procurement with purchase orders, goods received notes, and full supplier relationship tracking.',
          features: ['Purchase orders & GRN', 'Supplier management', 'Landed cost & cost tracking'],
        },
        {
          icon: 'clipboard-list', color: '#0891b2', bg: 'rgba(8,145,178,.1)',
          title: 'Procurement',
          desc: 'End-to-end procurement lifecycle — vendor management, sourcing events, purchase requisitions, and purchase orders with approval workflows.',
          features: ['Vendor directory & categories', 'Purchase requisitions & approvals', 'Sourcing events & purchase orders'],
        },
        {
          icon: 'file-invoice', color: '#7c3aed', bg: 'rgba(124,58,237,.1)',
          title: 'Invoicing',
          desc: 'Generate professional, branded invoices, track payment status in real-time, and send automated payment reminders.',
          features: ['Invoice generation & delivery', 'Payment status tracking', 'Invoice history & reporting'],
        },
        {
          icon: 'credit-card', color: '#0d9488', bg: 'rgba(13,148,136,.1)',
          title: 'Billing',
          desc: 'Manage client billing cycles, generate statements, and reconcile payments with a complete billing audit trail.',
          features: ['Billing cycle management', 'Payment reconciliation', 'Billing history & statements'],
        },
        {
          icon: 'calculator', color: '#2563eb', bg: 'rgba(37,99,235,.1)',
          title: 'Accounting',
          desc: 'Full double-entry accounting with chart of accounts, automated journal entries, and complete financial statement generation.',
          features: ['Chart of accounts & journals', 'Trial balance & general ledger', 'P&L, balance sheet & cash flow'],
        },
        {
          icon: 'users', color: '#dc2626', bg: 'rgba(220,38,38,.1)',
          title: 'HR Management',
          desc: 'Manage your entire workforce — employee records, departments, attendance, leave requests, and document management.',
          features: ['Employee profiles & departments', 'Leave & attendance tracking', 'Roles & permissions management'],
        },
        {
          icon: 'coins', color: '#d97706', bg: 'rgba(217,119,6,.1)',
          title: 'Payroll',
          desc: 'Automate payroll runs with configurable allowances, deductions, and generate digital payslips your employees can access anytime.',
          features: ['Payroll period management', 'Custom earnings & deductions', 'Digital payslip generation'],
        },
        {
          icon: 'hand-holding-usd', color: '#059669', bg: 'rgba(5,150,105,.1)',
          title: 'Loan Management',
          desc: 'Handle employee loan requests, approvals, repayment schedules, and automate deductions through the payroll engine.',
          features: ['Loan requests & approvals', 'Repayment scheduling', 'Payroll-integrated deductions'],
        },
        {
          icon: 'address-card', color: '#be123c', bg: 'rgba(190,18,60,.1)',
          title: 'Debtors',
          desc: 'Track customers who purchased on credit, monitor outstanding balances, and generate customer account statements.',
          features: ['Credit sales tracking', 'Outstanding balance reports', 'Customer statements'],
        },
        {
          icon: 'landmark', color: '#0891b2', bg: 'rgba(8,145,178,.1)',
          title: 'Creditors',
          desc: 'Manage supplier payables, track amounts owed, schedule payments, and maintain accurate supplier reconciliations.',
          features: ['Supplier payables tracking', 'Payment scheduling', 'Supplier reconciliation'],
        },
        {
          icon: 'chart-bar', color: '#475569', bg: 'rgba(71,85,105,.1)',
          title: 'Reports & Analytics',
          desc: 'Comprehensive reporting across every module — from daily sales summaries to full financial dashboards and custom date-range reports.',
          features: ['Sales, purchase & inventory reports', 'Financial statements & P&L', 'Custom date-range analytics'],
        },
        {
          icon: 'cog', color: '#6d28d9', bg: 'rgba(109,40,217,.1)',
          title: 'Admin & Settings',
          desc: 'Configure your company, branches, and users. Define roles with granular permissions and maintain a full system audit trail.',
          features: ['Multi-user & role management', 'Company & branch configuration', 'Audit logs & access control'],
        },
        {
          icon: 'layer-group', color: '#7c3aed', bg: 'rgba(124,58,237,.08)',
          title: 'Asset Management',
          desc: 'Register, categorise and track every business asset from purchase through to disposal — with automated depreciation and full custody trails.',
          features: ['Asset registration & categories', 'SLM / Reducing balance depreciation', 'Allocation, maintenance & disposal'],
        },
        {
          icon: 'id-badge', color: '#0f766e', bg: 'rgba(15,118,110,.1)',
          title: 'Visitor Management',
          desc: 'Control and track every visitor from pre-registration to sign-out — with QR-code check-in, digital badges, and a live reception dashboard.',
          features: ['Pre-registration & QR check-in', 'Live reception dashboard', 'Visit history & audit log'],
        },
        {
          icon: 'comments', color: '#0891b2', bg: 'rgba(8,145,178,.1)',
          title: 'Team Chat',
          desc: 'Built-in real-time messaging for your entire workforce — direct messages, group chats, and document sharing without leaving the platform.',
          features: ['Direct & group messaging', 'File & document sharing', 'Real-time notifications'],
        },
        {
          icon: 'calendar-week', color: '#059669', bg: 'rgba(5,150,105,.1)',
          title: 'Shift & Rota',
          desc: 'Plan and publish employee shifts, manage weekly rotas, and track team availability across departments and locations.',
          features: ['Shift scheduling & rota builder', 'Employee availability tracking', 'Instant schedule notifications'],
        },
      ],

      // ── Procurement spotlight cards ──────────────────────────────────
      procurementSpotlight: [
        { icon: 'building',       color: '#0891b2', bg: 'rgba(8,145,178,.1)',   title: 'Vendor Management',     desc: 'Maintain a vendor directory with categories, contacts, documents, and performance history.' },
        { icon: 'clipboard-list', color: '#ea580c', bg: 'rgba(234,88,12,.1)',   title: 'Purchase Requisitions', desc: 'Raise and route purchase requests through configurable multi-step approval workflows.' },
        { icon: 'file-invoice',   color: '#7c3aed', bg: 'rgba(124,58,237,.1)',  title: 'Purchase Orders',       desc: 'Convert approved requisitions into purchase orders and send directly to vendors.' },
        { icon: 'handshake',      color: '#059669', bg: 'rgba(5,150,105,.1)',   title: 'Sourcing Events',       desc: 'Run RFQ and competitive sourcing events to get the best value from your supply chain.' },
      ],

      // ── Finance spotlight cards ──────────────────────────────────────
      financeSpotlight: [
        { icon: 'calculator',   color: '#2563eb', bg: 'rgba(37,99,235,.1)',   title: 'Accounting',  desc: 'Double-entry journals, chart of accounts, trial balance & financial statements.' },
        { icon: 'file-invoice', color: '#7c3aed', bg: 'rgba(124,58,237,.1)', title: 'Invoicing',   desc: 'Generate, send and track invoices with automated payment reminders.' },
        { icon: 'credit-card',  color: '#0d9488', bg: 'rgba(13,148,136,.1)', title: 'Billing',     desc: 'Recurring billing cycles, statements and payment reconciliation.' },
        { icon: 'address-card', color: '#be123c', bg: 'rgba(190,18,60,.1)',  title: 'Debtors & Creditors', desc: 'Full visibility over what you are owed and what you owe.' },
      ],

      // ── HR spotlight cards ───────────────────────────────────────────
      hrSpotlight: [
        { icon: 'users',           color: '#dc2626', bg: 'rgba(220,38,38,.1)',   title: 'HR Management',    desc: 'Employee records, departments, leave requests and attendance tracking.' },
        { icon: 'coins',           color: '#d97706', bg: 'rgba(217,119,6,.1)',   title: 'Payroll',          desc: 'Automated payroll with custom earnings, deductions and digital payslips.' },
        { icon: 'hand-holding-usd', color: '#059669', bg: 'rgba(5,150,105,.1)', title: 'Loan Management',  desc: 'Loan requests, approvals and payroll-integrated repayment deductions.' },
      ],

      // ── Operations spotlight cards ───────────────────────────────────
      opsSpotlight: [
        { icon: 'boxes',         color: '#004AAD', bg: 'rgba(0,74,173,.1)',   title: 'Inventory',  desc: 'Real-time stock across all warehouses with barcodes, batches and expiry dates.' },
        { icon: 'shopping-cart', color: '#16a34a', bg: 'rgba(22,163,74,.1)', title: 'Sales',      desc: 'Full sales cycle management — orders, POS, reversals and credit tracking.' },
        { icon: 'truck',         color: '#ea580c', bg: 'rgba(234,88,12,.1)', title: 'Purchases',  desc: 'Purchase orders, GRN, supplier management and cost tracking built in.' },
      ],

      // ── Asset spotlight cards ────────────────────────────────────────
      assetSpotlight: [
        { icon: 'tags',         color: '#7c3aed', bg: 'rgba(124,58,237,.1)', title: 'Registration & Categories',  desc: 'Log assets with full details — serial numbers, purchase cost, location and category.' },
        { icon: 'chart-line',   color: '#2563eb', bg: 'rgba(37,99,235,.1)',  title: 'Depreciation Engine',        desc: 'Straight-line, reducing balance, and usage-based depreciation calculated automatically.' },
        { icon: 'map-marker-alt', color: '#ea580c', bg: 'rgba(234,88,12,.1)', title: 'Allocation & Custody',     desc: 'Assign assets to employees or departments and track custody changes over time.' },
        { icon: 'tools',        color: '#059669', bg: 'rgba(5,150,105,.1)',  title: 'Maintenance & Disposal',     desc: 'Log maintenance jobs, loss and damage cases, and manage end-of-life disposals.' },
      ],

      // ── Chat spotlight cards ─────────────────────────────────────────
      chatSpotlight: [
        { icon: 'comment-dots', color: '#0891b2', bg: 'rgba(8,145,178,.1)',  title: 'Direct Messaging',   desc: 'One-to-one conversations with any colleague in your organisation, instantly.' },
        { icon: 'users',        color: '#7c3aed', bg: 'rgba(124,58,237,.1)', title: 'Group Channels',     desc: 'Create department or project group chats and keep conversations organised.' },
        { icon: 'paperclip',    color: '#d97706', bg: 'rgba(217,119,6,.1)',  title: 'File Sharing',       desc: 'Share documents, images and files directly in chat — stored securely in the cloud.' },
        { icon: 'plug',         color: '#16a34a', bg: 'rgba(22,163,74,.1)',  title: 'Real-time Delivery', desc: 'Messages and notifications delivered instantly via WebSockets — no page refresh needed.' },
      ],

      // ── Visitor spotlight cards ──────────────────────────────────────
      visitorSpotlight: [
        { icon: 'user-plus',     color: '#0f766e', bg: 'rgba(15,118,110,.1)', title: 'Pre-Registration',      desc: 'Employees invite visitors in advance — details captured before they arrive at reception.' },
        { icon: 'qrcode',        color: '#2563eb', bg: 'rgba(37,99,235,.1)',  title: 'QR Check-In',           desc: 'Visitors scan a QR code on arrival for instant, contactless check-in with no manual entry.' },
        { icon: 'id-card',       color: '#d97706', bg: 'rgba(217,119,6,.1)',  title: 'Digital Visitor Badge',  desc: 'Auto-generated badge with name, host, purpose and time — printable or shown on screen.' },
        { icon: 'tachometer-alt', color: '#7c3aed', bg: 'rgba(124,58,237,.1)', title: 'Live Reception Dashboard', desc: 'See who is on-site, expected arrivals, and today\'s check-out status at a glance.' },
      ],

      // ── Mock visitor preview ─────────────────────────────────────────
      visitorPreview: [
        { initials: 'TA', name: 'Tunde Adeyemi',  host: 'HR Manager',    time: '09:15', status: 'On-site',   cls: 'svc-tag--green'  },
        { initials: 'LW', name: 'Laura Williams', host: 'CEO',           time: '10:00', status: 'Expected',  cls: 'svc-tag--blue'   },
        { initials: 'BN', name: 'Ben Nwosu',      host: 'Sales Lead',    time: '11:30', status: 'On-site',   cls: 'svc-tag--green'  },
        { initials: 'AF', name: 'Amira Fawzi',    host: 'IT Director',   time: '14:00', status: 'Signed Out', cls: 'svc-tag--orange' },
      ],

      // ── Shift & Rota spotlight cards ─────────────────────────────────
      shiftSpotlight: [
        { icon: 'calendar-alt',  color: '#059669', bg: 'rgba(5,150,105,.1)',  title: 'Shift Scheduling',    desc: 'Create and assign shifts by employee, department, or location with flexible time slots.' },
        { icon: 'table',         color: '#2563eb', bg: 'rgba(37,99,235,.1)',  title: 'Rota Builder',        desc: 'Build weekly rotas at a glance and publish them to your team with one click.' },
        { icon: 'user-check',    color: '#7c3aed', bg: 'rgba(124,58,237,.1)', title: 'Availability Tracking', desc: 'Know who is available, on leave, or already scheduled before you assign a shift.' },
        { icon: 'bell',          color: '#d97706', bg: 'rgba(217,119,6,.1)',  title: 'Instant Notifications', desc: 'Employees receive shift confirmations and rota updates in real time.' },
      ],

      // ── Mock payroll preview ─────────────────────────────────────────
      payrollPreview: [
        { initials: 'JD', name: 'James Doe',    role: 'Senior Engineer',  pay: '£4,200.00' },
        { initials: 'SA', name: 'Sarah Adebayo', role: 'HR Manager',      pay: '£3,600.00' },
        { initials: 'MK', name: 'Mark Kamara',  role: 'Accountant',       pay: '£3,100.00' },
        { initials: 'FO', name: 'Faith Okon',   role: 'Sales Executive',  pay: '£2,800.00' },
      ],

      // ── Mock asset preview ───────────────────────────────────────────
      assetPreview: [
        { name: 'IT Equipment',    count: '86 assets',  pct: 87, color: '#7c3aed' },
        { name: 'Office Furniture', count: '54 assets', pct: 55, color: '#2563eb' },
        { name: 'Vehicles',        count: '12 assets',  pct: 24, color: '#ea580c' },
        { name: 'Machinery',       count: '96 assets',  pct: 96, color: '#16a34a' },
      ],

      // ── Mock chat preview ────────────────────────────────────────────
      chatPreview: [
        { initials: 'SA', name: 'Sarah Adebayo', text: 'Can you review the payroll report before EOD?', time: '10:14', color: '#7c3aed' },
        { initials: 'MK', name: 'Mark Kamara',   text: 'Done! Just uploaded the updated version.', time: '10:16', color: '#0891b2' },
        { initials: 'FO', name: 'Faith Okon',    text: 'Rota for next week has been published.', time: '10:21', color: '#059669' },
      ],

      // ── Mock rota preview ────────────────────────────────────────────
      rotaPreview: [
        { initials: 'JD', name: 'James Doe',    shift: 'Morning — 08:00–16:00', day: 'Mon',  cls: 'svc-rota-badge--green',  color: '#004AAD' },
        { initials: 'SA', name: 'Sarah Adebayo', shift: 'Afternoon — 12:00–20:00', day: 'Tue', cls: 'svc-rota-badge--blue',   color: '#7c3aed' },
        { initials: 'MK', name: 'Mark Kamara',  shift: 'Night — 20:00–06:00',    day: 'Wed', cls: 'svc-rota-badge--orange', color: '#0891b2' },
        { initials: 'FO', name: 'Faith Okon',   shift: 'Morning — 08:00–16:00',  day: 'Mon', cls: 'svc-rota-badge--green',  color: '#059669' },
      ],

      // ── Mock procurement preview ─────────────────────────────────────
      procurementPreview: [
        { ref: 'PR-0042', item: 'Office Laptops × 10',     vendor: 'TechMart Ltd',    status: 'Approved', cls: 'svc-tag--green'  },
        { ref: 'PO-0018', item: 'Printer Cartridges × 50', vendor: 'OfficeBase Co.',  status: 'Sent',     cls: 'svc-tag--blue'   },
        { ref: 'PO-0017', item: 'Standing Desks × 5',      vendor: 'FurnishPro Ltd',  status: 'Received', cls: 'svc-tag--purple' },
        { ref: 'PR-0041', item: 'Safety Helmets × 20',     vendor: '—',               status: 'Pending',  cls: 'svc-tag--orange' },
      ],

      // ── Mock inventory preview ───────────────────────────────────────
      inventoryPreview: [
        { name: 'Office Supplies',    qty: '842 units',  pct: 84, color: '#16a34a' },
        { name: 'Electronic Parts',   qty: '210 units',  pct: 21, color: '#d97706' },
        { name: 'Packaging Material', qty: '1,200 units', pct: 96, color: '#16a34a' },
        { name: 'Raw Materials',      qty: '63 units',   pct: 12, color: '#dc2626' },
      ],

      // ── Value props ──────────────────────────────────────────────────
      valueProps: [
        { color: '#004AAD', title: 'Quick, Accurate, Validated.',   desc: 'Reduce errors and save hours on routine business tasks with intelligent automation.' },
        { color: '#16a34a', title: 'Guaranteed 100% Uptime.',       desc: '24/7 access to all your data from anywhere in the world, on any device.' },
        { color: '#e08c00', title: 'All Your Data, Forever.',       desc: 'Lifetime storage with no expiry on your records — your data is always yours.' },
        { color: '#7c3aed', title: 'Enterprise-Grade Security.',    desc: 'End-to-end encryption and role-based access control keep your data safe.' },
      ],

      pillars: [
        { img: 'static/img/icon1.png', title: 'Deeply Integrated',  desc: 'Every module connects seamlessly — a sale updates inventory, creates a journal entry, and generates an invoice automatically.' },
        { img: 'static/img/icon2.png', title: 'Hardware Compatible', desc: 'Works with barcode scanners, receipt printers, and RFID equipment out of the box.' },
        { img: 'static/img/icon3.png', title: 'Infinitely Scalable', desc: 'Multi-warehouse, multi-branch, multi-currency and multi-user support grows with your business.' },
      ],
    };
  },

  methods: {
    pageData() {
      axios.get('/admin/fetchProjectSetup')
        .then(({ data }) => { this.setup = data.data; })
        .catch(() => {});
    },

    async sendEnquiry() {
      this.enquiryLoading = true;
      try {
        await axios.post('/send-enquiry', {
          name:         this.enquiry_name,
          email:        this.enquiry_email,
          phone_number: this.enquiry_tel,
          message:      this.enquiry_message,
        });
        this.enquirySent     = true;
        this.enquiry_name    = '';
        this.enquiry_email   = '';
        this.enquiry_tel     = '';
        this.enquiry_message = '';
      } catch (err) {
        this.toast.error(err.response?.data?.message ?? 'An error occurred. Please try again.');
      } finally {
        this.enquiryLoading = false;
      }
    },
  },

  async mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    await this.initPricing();
    this.plansLoading = false;
    this.pageData();
    if (this.superUser) this.superUser();
  },
};
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────────────────── */
.page-container { max-width:1160px; margin:0 auto; padding:0 24px; }
.section-pad    { padding:80px 0; }
.section-gray   { background:#f4f7fb; }
.section-header { text-align:center; max-width:680px; margin:0 auto 56px; }
.section-eyebrow { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:#004AAD; margin:0 0 12px; }
.section-title  { font-size:clamp(24px,4vw,36px); font-weight:700; color:#1a2b45; margin:0 0 16px; line-height:1.25; }
.section-body   { font-size:15px; color:#6b7a99; line-height:1.7; margin:0; }
.accent { color:#004AAD; }
.me-1   { margin-right:4px; }
.ms-2   { margin-left:8px; }

/* ── Buttons ─────────────────────────────────────────────────────────── */
.btn-primary-lg { display:inline-flex; align-items:center; justify-content:center; gap:8px; background:#004AAD; color:#fff; font-size:15px; font-weight:600; padding:13px 28px; border-radius:10px; text-decoration:none; border:none; cursor:pointer; transition:background .15s, transform .1s; white-space:nowrap; }
.btn-primary-lg:hover:not(:disabled) { background:#003b8e; color:#fff; text-decoration:none; }
.btn-primary-lg:active:not(:disabled) { transform:scale(.97); }
.btn-primary-lg:disabled { opacity:.55; cursor:not-allowed; }
.btn-primary-lg.btn-white { background:#fff; color:#004AAD; }
.btn-primary-lg.btn-white:hover { background:#e8f0fb; }
.btn-primary-lg.w-full { width:100%; }
.btn-primary-md { display:inline-flex; align-items:center; gap:8px; background:#004AAD; color:#fff; font-size:14px; font-weight:600; padding:10px 22px; border-radius:9px; text-decoration:none; border:none; cursor:pointer; transition:background .15s; }
.btn-primary-md:hover { background:#003b8e; color:#fff; text-decoration:none; }
.btn-ghost-lg   { display:inline-flex; align-items:center; gap:8px; background:transparent; color:#1a2b45; border:1px solid #dde5f0; font-size:15px; font-weight:600; padding:13px 28px; border-radius:10px; text-decoration:none; cursor:pointer; transition:all .15s; }
.btn-ghost-lg:hover { background:#f4f7fb; text-decoration:none; color:#004AAD; }

/* ── Hero ────────────────────────────────────────────────────────────── */
.hero-section { padding:120px 0 80px; background:linear-gradient(135deg,#f0f6ff 0%,#fafcff 60%,#fff 100%); }
.hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:56px; align-items:center; }
.hero-badge   { display:inline-flex; align-items:center; gap:6px; background:rgba(0,74,173,.08); color:#004AAD; border:1px solid rgba(0,74,173,.18); border-radius:20px; padding:5px 14px; font-size:13px; font-weight:600; margin-bottom:20px; }
.hero-title   { font-size:clamp(32px,5vw,52px); font-weight:800; color:#1a2b45; line-height:1.15; margin:0 0 20px; }
.hero-sub     { font-size:16px; color:#6b7a99; line-height:1.7; margin:0 0 32px; max-width:480px; }
.hero-actions { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
.hero-visual img { width:100%; max-width:560px; }

/* ── Stats bar ───────────────────────────────────────────────────────── */
.stats-bar  { background:#004AAD; padding:28px 0; }
.stats-grid { display:flex; align-items:center; justify-content:center; gap:0; flex-wrap:wrap; }
.stat-item  { display:flex; flex-direction:column; align-items:center; gap:4px; padding:12px 36px; }
.stat-number { font-size:20px; font-weight:800; color:#fff; }
.stat-label  { font-size:12px; font-weight:500; color:rgba(255,255,255,.7); text-align:center; }
.stat-divider { width:1px; height:40px; background:rgba(255,255,255,.25); }

/* ── Split ───────────────────────────────────────────────────────────── */
.split-grid { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
.split-grid--reverse .split-content { order:2; }
.split-grid--reverse .split-visual  { order:1; }
.split-content { display:flex; flex-direction:column; gap:20px; }
.split-content .section-title { margin-bottom:0; }
.split-visual img { width:100%; max-width:500px; }

/* ── Check list ──────────────────────────────────────────────────────── */
.check-list { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:10px; }
.check-list li { display:flex; align-items:center; gap:10px; font-size:14px; font-weight:500; color:#1a2b45; }
.check-icon { color:#16a34a; font-size:14px; flex-shrink:0; }

/* ── Modules grid ────────────────────────────────────────────────────── */
.modules-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(300px, 1fr)); gap:20px; }
.module-card  {
  background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:24px;
  display:flex; gap:16px; align-items:flex-start;
  transition:box-shadow .2s, border-color .2s, transform .2s;
}
.module-card:hover { box-shadow:0 10px 32px rgba(0,0,0,.09); border-color:#c5d6f5; transform:translateY(-3px); }
.module-icon-wrap { width:46px; height:46px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.module-body  { flex:1; min-width:0; }
.module-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.module-desc  { font-size:13px; color:#6b7a99; line-height:1.55; margin:0 0 12px; }
.module-features { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:6px; }
.module-features li { display:flex; align-items:center; gap:8px; font-size:12.5px; color:#3d5068; font-weight:500; }
.mf-check { color:#16a34a; font-size:10px; flex-shrink:0; }

/* ── Spotlight cards ─────────────────────────────────────────────────── */
.spotlight-cards { display:flex; flex-direction:column; gap:12px; }
.spotlight-card  { display:flex; align-items:flex-start; gap:14px; padding:14px 16px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; }
.spotlight-card-icon { width:36px; height:36px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.spotlight-card-title { font-size:13.5px; font-weight:700; color:#1a2b45; margin-bottom:2px; }
.spotlight-card-desc  { font-size:12.5px; color:#6b7a99; line-height:1.5; }

/* ── Spotlight visual card ───────────────────────────────────────────── */
.spotlight-visual-card {
  background:#fff; border:1px solid #e4eaf2; border-radius:16px;
  padding:24px; box-shadow:0 8px 32px rgba(0,0,0,.08);
}
.svc-header { display:flex; align-items:center; gap:10px; font-size:14px; font-weight:700; color:#1a2b45; margin-bottom:20px; }
.svc-header-icon { font-size:18px; color:#004AAD; }

.svc-metric-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:14px; }
.svc-metric { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:14px; }
.svc-metric-label { display:block; font-size:11.5px; color:#7a90b0; font-weight:600; text-transform:uppercase; letter-spacing:.04em; margin-bottom:6px; }
.svc-metric-value { font-size:18px; font-weight:800; color:#1a2b45; }

.svc-progress-row { display:flex; align-items:center; gap:10px; margin-bottom:16px; }
.svc-progress-label { font-size:12px; color:#7a90b0; font-weight:600; white-space:nowrap; }
.svc-progress-bar { flex:1; height:8px; background:#e4eaf2; border-radius:4px; overflow:hidden; }
.svc-progress-fill { height:100%; background:#004AAD; border-radius:4px; transition:width .4s; }
.svc-progress-pct { font-size:12px; font-weight:700; color:#004AAD; white-space:nowrap; }

.svc-tag-row, .svc-hr-tags { display:flex; flex-wrap:wrap; gap:6px; }
.svc-tag { display:inline-flex; align-items:center; padding:3px 10px; border-radius:20px; font-size:11.5px; font-weight:600; }
.svc-tag--green  { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
.svc-tag--blue   { background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; }
.svc-tag--purple { background:#faf5ff; color:#7c3aed; border:1px solid #ddd6fe; }
.svc-tag--orange { background:#fff7ed; color:#ea580c; border:1px solid #fed7aa; }
.svc-tag--red    { background:#fff0f0; color:#dc2626; border:1px solid #fecaca; }
.svc-tag--teal   { background:#f0fdfa; color:#0f766e; border:1px solid #99f6e4; }

.svc-visitor-row  { display:flex; align-items:center; gap:10px; padding:8px 10px; background:#f8fafc; border-radius:9px; margin-bottom:6px; }
.svc-visitor-info { flex:1; min-width:0; }
.svc-visitor-host { font-size:11.5px; color:#7a90b0; margin-top:1px; }

.svc-payroll-list { display:flex; flex-direction:column; gap:10px; margin-bottom:16px; }
.svc-payroll-row  { display:flex; align-items:center; gap:12px; padding:10px; background:#f8fafc; border-radius:9px; }
.svc-emp-avatar   { width:36px; height:36px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
.svc-emp-info     { flex:1; display:flex; flex-direction:column; }
.svc-emp-name     { font-size:13px; font-weight:600; color:#1a2b45; }
.svc-emp-role     { font-size:11.5px; color:#7a90b0; }
.svc-emp-pay      { font-size:13px; font-weight:700; color:#16a34a; white-space:nowrap; }

.svc-inv-list { display:flex; flex-direction:column; gap:10px; margin-bottom:16px; }
.svc-inv-row  { display:flex; align-items:center; gap:10px; }
.svc-inv-name { font-size:12.5px; font-weight:600; color:#1a2b45; width:140px; flex-shrink:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.svc-inv-bar-wrap { flex:1; }
.svc-inv-bar  { height:7px; background:#e4eaf2; border-radius:4px; overflow:hidden; }
.svc-inv-fill { height:100%; border-radius:4px; transition:width .4s; }
.svc-inv-qty  { font-size:12px; font-weight:700; white-space:nowrap; min-width:72px; text-align:right; }

/* ── Procurement pipeline list ───────────────────────────────────────── */
.svc-po-list { display:flex; flex-direction:column; gap:8px; margin-bottom:16px; }
.svc-po-row  { display:flex; align-items:center; gap:10px; padding:10px 12px; background:#f8fafc; border-radius:9px; }
.svc-po-ref  { font-size:11.5px; font-weight:700; color:#7a90b0; min-width:58px; flex-shrink:0; font-variant-numeric:tabular-nums; }

/* ── Chat messages ───────────────────────────────────────────────────── */
.svc-chat-list { display:flex; flex-direction:column; gap:10px; margin-bottom:16px; }
.svc-chat-msg  { display:flex; align-items:flex-start; gap:10px; padding:10px; background:#f8fafc; border-radius:9px; }
.svc-chat-bubble { flex:1; min-width:0; }
.svc-chat-text { font-size:12.5px; color:#6b7a99; line-height:1.45; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.svc-chat-time { font-size:11px; color:#a0aec0; white-space:nowrap; flex-shrink:0; padding-top:2px; }

/* ── Rota rows ───────────────────────────────────────────────────────── */
.svc-rota-list { display:flex; flex-direction:column; gap:10px; margin-bottom:16px; }
.svc-rota-row  { display:flex; align-items:center; gap:12px; padding:10px; background:#f8fafc; border-radius:9px; }
.svc-rota-badge { padding:3px 10px; border-radius:20px; font-size:11.5px; font-weight:700; flex-shrink:0; }
.svc-rota-badge--green  { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
.svc-rota-badge--blue   { background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; }
.svc-rota-badge--orange { background:#fff7ed; color:#ea580c; border:1px solid #fed7aa; }

/* ── Value props ─────────────────────────────────────────────────────── */
.value-props { display:flex; flex-direction:column; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; background:#fff; }
.value-prop  { display:flex; align-items:center; gap:16px; padding:20px 24px; border-bottom:1px solid #f0f4f8; }
.value-prop:last-child { border-bottom:none; }
.value-dot   { width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; color:#fff; flex-shrink:0; }
.value-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.value-desc  { font-size:13px; color:#6b7a99; margin:0; }

/* ── CTA band ────────────────────────────────────────────────────────── */
.cta-band       { background:#004AAD; padding:56px 0; }
.cta-band-inner { display:flex; align-items:center; justify-content:space-between; gap:24px; flex-wrap:wrap; }
.cta-title      { font-size:clamp(20px,3vw,28px); font-weight:700; color:#fff; margin:0 0 8px; }
.cta-sub        { font-size:15px; color:rgba(255,255,255,.75); margin:0; }

/* ── Pillars ─────────────────────────────────────────────────────────── */
.pillars-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:24px; }
.pillar-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:32px 28px; text-align:center; transition:box-shadow .2s, transform .2s; }
.pillar-card:hover { box-shadow:0 12px 32px rgba(0,0,0,.1); transform:translateY(-4px); }
.pillar-img   { height:72px; object-fit:contain; margin-bottom:20px; }
.pillar-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 10px; }
.pillar-desc  { font-size:13.5px; color:#6b7a99; line-height:1.6; margin:0; }

/* ── Pricing ─────────────────────────────────────────────────────────── */
.plans-loading      { display:flex; flex-direction:column; align-items:center; gap:14px; padding:60px 0; }
.plans-loading-text { font-size:14px; color:#7a90b0; margin:0; }
.plans-empty        { text-align:center; padding:48px 0; font-size:14px; color:#7a90b0; }

.billing-tabs { display:flex; justify-content:center; gap:6px; margin-bottom:48px; flex-wrap:wrap; }
.billing-tab  { padding:8px 20px; border-radius:8px; border:1px solid #dde5f0; background:#fff; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; transition:all .15s; }
.billing-tab:hover { background:#f4f7fb; color:#1a2b45; }
.billing-tab--active { background:#004AAD; color:#fff; border-color:#004AAD; }

.plans-grid  { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
.plan-card   { position:relative; background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:28px 24px; display:flex; flex-direction:column; transition:box-shadow .2s, border-color .2s; }
.plan-card:hover { box-shadow:0 12px 32px rgba(0,0,0,.1); border-color:#c5d6f5; }
.plan-card--popular { border-color:#004AAD; border-width:2px; box-shadow:0 8px 28px rgba(0,74,173,.14); }
.popular-badge { position:absolute; top:-13px; left:50%; transform:translateX(-50%); background:#004AAD; color:#fff; font-size:12px; font-weight:700; border-radius:20px; padding:3px 14px; white-space:nowrap; }
.plan-name     { font-size:14px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.06em; margin-bottom:16px; margin-top:8px; }
.plan-price    { display:flex; align-items:flex-start; gap:2px; }
.plan-currency { font-size:18px; font-weight:700; color:#1a2b45; margin-top:6px; }
.plan-amount   { font-size:40px; font-weight:800; color:#1a2b45; line-height:1; }
.plan-cycle    { font-size:12px; color:#7a90b0; font-weight:600; text-transform:uppercase; letter-spacing:.05em; margin-bottom:20px; }
.plan-features { list-style:none; padding:0; margin:0 0 24px; flex:1; display:flex; flex-direction:column; gap:9px; }
.plan-features li { display:flex; align-items:flex-start; gap:9px; font-size:13px; color:#3d5068; line-height:1.4; }
.plan-check    { color:#16a34a; font-size:11px; flex-shrink:0; margin-top:2px; }
.plan-cta      { width:100%; padding:11px 0; border-radius:9px; border:2px solid #004AAD; background:transparent; color:#004AAD; font-size:14px; font-weight:700; cursor:pointer; transition:all .15s; }
.plan-card--popular .plan-cta { background:#004AAD; color:#fff; }
.plan-cta:hover { background:#004AAD; color:#fff; }
.plan-card--popular .plan-cta:hover { background:#003b8e; border-color:#003b8e; }

/* ── Demo ────────────────────────────────────────────────────────────── */
.demo-wrap     { display:grid; grid-template-columns:1fr 1.2fr; gap:64px; align-items:start; }
.demo-copy     { display:flex; flex-direction:column; gap:16px; padding-top:16px; }
.demo-perks    { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:10px; }
.demo-perks li { display:flex; align-items:center; gap:10px; font-size:14px; color:#4a5568; }
.demo-form-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:32px; }
.form-card-title { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 20px; }
.form-row-2      { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.form-group-pub  { display:flex; flex-direction:column; gap:6px; margin-bottom:16px; }
.form-label-pub  { font-size:13px; font-weight:600; color:#1a2b45; }
.form-input-pub  { width:100%; padding:10px 14px; border:1px solid #dde5f0; border-radius:8px; font-size:14px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; box-sizing:border-box; }
.form-input-pub:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
textarea.form-input-pub { resize:vertical; }

/* ── Enquiry success ─────────────────────────────────────────────────── */
.enquiry-success       { display:flex; flex-direction:column; align-items:center; text-align:center; gap:14px; padding:20px 0; }
.enquiry-success-icon  { font-size:52px; color:#16a34a; }
.enquiry-success-title { font-size:20px; font-weight:700; color:#1a2b45; margin:0; }
.enquiry-success-sub   { font-size:14px; color:#7a90b0; margin:0; line-height:1.6; max-width:300px; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:1024px) {
  .modules-grid  { grid-template-columns:repeat(2,1fr); }
  .plans-grid    { grid-template-columns:repeat(2,1fr); }
  .pillars-grid  { grid-template-columns:repeat(2,1fr); }
  .demo-wrap     { grid-template-columns:1fr; gap:32px; }
  .stat-item     { padding:10px 20px; }
}
@media (max-width:768px) {
  .section-pad   { padding:56px 0; }
  .hero-section  { padding:100px 0 56px; }
  .hero-grid     { grid-template-columns:1fr; gap:32px; }
  .hero-visual   { display:none; }
  .split-grid    { grid-template-columns:1fr; gap:32px; }
  .split-grid--reverse .split-content { order:1; }
  .split-grid--reverse .split-visual  { order:2; }
  .modules-grid  { grid-template-columns:1fr; }
  .plans-grid    { grid-template-columns:1fr; }
  .pillars-grid  { grid-template-columns:1fr; }
  .cta-band-inner { flex-direction:column; align-items:flex-start; }
  .form-row-2    { grid-template-columns:1fr; }
  .stats-grid    { gap:0; flex-direction:column; }
  .stat-divider  { width:80%; height:1px; margin:0 auto; }
  .svc-metric-row { grid-template-columns:1fr; }
  .svc-inv-name  { width:100px; }
}

/* ── Quote CTA (replaces pricing section) ────────────────────────────────── */
.quote-cta-wrap {
  text-align: center;
  max-width: 680px;
  margin: 0 auto;
  padding: 20px 0;
}
.quote-cta-icon {
  width: 64px; height: 64px;
  border-radius: 50%;
  background: rgba(0,74,173,.1);
  color: #004AAD;
  font-size: 26px;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 24px;
}
.quote-cta-title {
  font-size: clamp(22px, 3.5vw, 32px);
  font-weight: 700;
  color: #1a2b45;
  margin: 0 0 16px;
}
.quote-cta-body {
  font-size: 15.5px;
  color: #6b7a99;
  line-height: 1.75;
  margin: 0 0 32px;
}
.btn-quote-cta {
  display: inline-flex; align-items: center; gap: 8px;
  background: #004AAD; color: #fff;
  padding: 13px 30px; border-radius: 10px;
  font-size: 15px; font-weight: 700;
  text-decoration: none; transition: background .15s;
}
.btn-quote-cta:hover { background: #003b8e; text-decoration: none; color: #fff; }
.ms-2 { margin-left: 8px; }
</style>
